import {BaseElement, OSkinableInit} from "back/commons/basis";
import {DOM, JSX} from "lib/commons/xml/dom";
import {ButtonActions, OButtonActionsInit} from "back/commons/widgets/buttons";
import {Action, ActionSeparator, IAction} from "lib/commons/actions";
import {REG} from "lib/commons/registry";

/**
 *
 */
export interface OInputPeriodInit extends OSkinableInit {

	defaultLabel?: string

	modes?: (InputPeriodAction | IAction<any> /* ACTION.SEPARATOR */)[]

	defaultMode?: InputPeriodAction
	defaultDate1?: number
	defaultDate2?: number
}

/** Interface isolée pour le lazyLoading de ce fichier. */
export interface IInputPeriod {

	isSpecified(): boolean

	getMoreThan(exclude: boolean): number | null

	getLessThan(exclude: boolean): number | null
}

export interface InputPeriod extends BaseElement, IInputPeriod {
	initialize(init?: OInputPeriodInit): this;
}

export class InputPeriod extends BaseElement implements IInputPeriod {

	mode: ButtonActions<InputPeriod>;
	date1: HTMLInputElement;
	betweenTxt: HTMLSpanElement;
	date2: HTMLInputElement;

	protected _currentMode: InputPeriodAction;

	set currentMode(v: InputPeriodAction) {
		this._currentMode = v;
		if (this.initialized) this.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
	}

	get currentMode(): InputPeriodAction {
		return this._currentMode;
	}

	protected _initialize(init: OInputPeriodInit): void {
		const reg = this.findReg(init);

		this.mode = <c-button-actions î={{
			reg,
			actions: init.modes || this.buildDefaultModes(),
			actionContext: this,
			label: init.defaultLabel || "Période..."
		} as OButtonActionsInit<InputPeriod>}/> as ButtonActions<InputPeriod>;
		this.date1 = <input type="date" hidden="true"/> as HTMLInputElement;
		this.betweenTxt = <span hidden="true"/>;
		this.date2 = <input type="date" hidden="true"/> as HTMLInputElement;

		this._attach(this.localName, init, this.mode, this.date1, this.betweenTxt, this.date2);

		if (init.defaultDate1 > 0) this.date1.valueAsNumber = init.defaultDate1;
		if (init.defaultDate2 > 0) this.date2.valueAsNumber = init.defaultDate2;
		if (init.defaultMode) init.defaultMode.execute(this);
	}

	/** Réinitialisation du mode et des dates */
	reset(modeId: string, date1: number, date2: number) {
		if (!this.initialized) throw "InputPeriod must be initialized for be reseted";
		this._currentMode = this.mode.actions.find(act => act.getId() === modeId) as InputPeriodAction;
		if (date1 > 0) this.date1.valueAsNumber = date1;
		if (date2 > 0) this.date2.valueAsNumber = date2;
		if (this._currentMode) this._currentMode.execute(this);
		this.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
	}

	isSpecified(): boolean {
		return this._currentMode ? this._currentMode.isSpecified(this) : false;
	}


	getMoreThan(exclude: boolean): number | null {
		return this._currentMode ? this._currentMode.getMoreThan(exclude, this) : null;
	}

	getLessThan(exclude: boolean): number | null {
		return this._currentMode ? this._currentMode.getLessThan(exclude, this) : null;
	}

	// Sélecteurs dédiés à une date qui n'a pas de sens dans le futur (ex : date de modification)
	private buildDefaultModes() {
		return [
			new InputPeriodSince("sinceToday", 0).setLabel("Aujourd'hui"),
			new InputPeriodSince("sinceYesterday", 1).setLabel("Depuis hier"),
			new InputPeriodSince("since1Week", 7).setLabel("Depuis une semaine"),
			new InputPeriodSince("since1Month", 31).setLabel("Depuis un mois"),
			new InputPeriodFrom("since").setLabel("Depuis le...").setSelectedLabel("Depuis le"),
			new ActionSeparator(),
			new InputPeriodOlder("older1Month", 31).setLabel("Il y a plus d'un mois"),
			new InputPeriodOlder("older6Month", 184).setLabel("Il y a plus de 6 mois"),
			new InputPeriodOlder("older1year", 365).setLabel("Il y a plus d'un an"),
			new InputPeriodBefore("before").setLabel("Avant le...").setSelectedLabel("Avant le"),
			new ActionSeparator(),
			new InputPeriodFromTo("between").setLabel("Entre le...").setSelectedLabel("Entre le"),
			new ActionSeparator(),
			new InputPeriod_None("none").setLabel("Aucune limite").setSelectedLabel("Période..."),
		];
	}

	// Sélecteurs dédiés à une date qui peut être définie dans le futur (ex : date de péremption, ...)
	static buildDefaultModes_futurAndPast() {
		return [
			new InputPeriodOlder("beforeToday", 0).setLabel("Avant aujourd'hui"),
			new InputPeriodOlder("beforeTomorrow", -1).setLabel("Avant demain"),
			new InputPeriodOlder("before1Week", -7).setLabel("Avant une semaine"),
			new InputPeriodOlder("before1Month", -31).setLabel("Avant un mois"),
			new InputPeriodOlder("older1Month", 31).setLabel("Il y a plus d'un mois"),
			new InputPeriodOlder("older6Month", 184).setLabel("Il y a plus de 6 mois"),
			new InputPeriodOlder("older1year", 365).setLabel("Il y a plus d'un an"),
			new InputPeriodBefore("before").setLabel("Avant le...").setSelectedLabel("Avant le"),
			new ActionSeparator(),
			new InputPeriodSince("sinceToday", 0).setLabel("À partir d'aujourd'hui"),
			new InputPeriodSince("sinceYesterday", -1).setLabel("À partir de demain"),
			new InputPeriodSince("since1Week", -7).setLabel("Dans une semaine ou plus"),
			new InputPeriodSince("since1Month", -31).setLabel("Dans un mois ou plus"),
			new InputPeriodSince("sinceYesterday", 1).setLabel("Depuis hier"),
			new InputPeriodSince("since1Week", 7).setLabel("Depuis une semaine"),
			new InputPeriodSince("since1Month", 31).setLabel("Depuis un mois"),
			new InputPeriodFrom("since").setLabel("Depuis le...").setSelectedLabel("Depuis le"),
			new ActionSeparator(),
			new InputPeriodFromTo("between").setLabel("Entre le...").setSelectedLabel("Entre le"),
			new ActionSeparator(),
			new InputPeriod_None("none").setLabel("Aucune limite").setSelectedLabel("Période..."),
		];
	}

	// Sélecteurs dédiés à une date qui peut être définie dans le futur (ex : date de péremption, ...)
	static buildDefaultModes_futur() {
		return [
			new InputPeriodOlder("beforeToday", 0).setLabel("Avant aujourd'hui"),
			new InputPeriodOlder("beforeTomorrow", -1).setLabel("Avant demain"),
			new InputPeriodOlder("before1Week", -7).setLabel("Avant une semaine"),
			new InputPeriodOlder("before1Month", -31).setLabel("Avant un mois"),
			new InputPeriodBefore("before").setLabel("Avant le...").setSelectedLabel("Avant le"),
			new ActionSeparator(),
			new InputPeriodSince("sinceToday", 0).setLabel("À partir d'aujourd'hui"),
			new InputPeriodSince("sinceYesterday", -1).setLabel("À partir de demain"),
			new InputPeriodSince("since1Week", -7).setLabel("Dans une semaine ou plus"),
			new InputPeriodSince("since1Month", -31).setLabel("Dans un mois ou plus"),
			new InputPeriodFrom("since").setLabel("Depuis le...").setSelectedLabel("Depuis le"),
			new ActionSeparator(),
			new InputPeriodFromTo("between").setLabel("Entre le...").setSelectedLabel("Entre le"),
			new ActionSeparator(),
			new InputPeriod_None("none").setLabel("Aucune limite").setSelectedLabel("Période..."),
		];
	}
}

REG.reg.registerSkin('c-input-period', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	input {
		margin-inline-start: .5em;
		margin-inline-end: .5em;
	}

	:host(:not([disabled])) c-button-actions {
		background-color: var(--form-bgcolor);
		border: 1px solid var(--border-color);
	}
`);

customElements.define("c-input-period", InputPeriod);

/**
 *
 */
export abstract class InputPeriodAction extends Action<InputPeriod> {

	selectedLabel: string;

	setSelectedLabel(label: string): this {
		this.selectedLabel = label;
		return this;
	}

	isSpecified(ctx: InputPeriod) {return true}

	getMoreThan(exclude: boolean, ctx: InputPeriod): number | null {return null};

	getLessThan(exclude: boolean, ctx: InputPeriod): number | null {return null};

	execute(ctx: InputPeriod, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		ctx.mode.label = this.selectedLabel || this.getLabel(ctx);
		ctx.currentMode = this;
	}
}

export class InputPeriod_None extends InputPeriodAction {

	isSpecified(ctx: InputPeriod) {return false}

	execute(ctx: InputPeriod, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		super.execute(ctx);
		DOM.setHidden(ctx.date1, true);
		DOM.setHidden(ctx.betweenTxt, true);
		DOM.setHidden(ctx.date2, true);
	}
}

export abstract class InputPeriod_0_Action extends InputPeriodAction {

	execute(ctx: InputPeriod, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		super.execute(ctx);
		DOM.setHidden(ctx.date1, true);
		DOM.setHidden(ctx.betweenTxt, true);
		DOM.setHidden(ctx.date2, true);
	}
}

export abstract class InputPeriod_1_Action extends InputPeriodAction {

	isSpecified(ctx: InputPeriod) {return !!ctx.date1.value}

	execute(ctx: InputPeriod, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		super.execute(ctx);
		DOM.setHidden(ctx.date1, false);
		DOM.setHidden(ctx.betweenTxt, true);
		DOM.setHidden(ctx.date2, true);
	}
}


export abstract class InputPeriod_2_Action extends InputPeriodAction {

	betweenLabel: string = " et le ";

	isSpecified(ctx: InputPeriod) {return !!ctx.date1.value && !!ctx.date2.value}

	setBetweenLabel(label: string): this {
		this.betweenLabel = label;
		return this;
	}

	execute(ctx: InputPeriod, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		super.execute(ctx);
		DOM.setHidden(ctx.date1, false);
		if (this.betweenLabel) {
			ctx.betweenTxt.textContent = this.betweenLabel;
			DOM.setHidden(ctx.betweenTxt, false);
		} else {
			DOM.setHidden(ctx.betweenTxt, true);
		}
		DOM.setHidden(ctx.date2, false);
	}
}


export class InputPeriodSince extends InputPeriod_0_Action {
	public days: number;

	constructor(id: string, days: number) {
		super(id);
		this.days = days;
	}

	getMoreThan(exclude: boolean, ctx: InputPeriod): number | null {
		const d = new Date();
		d.setDate(d.getDate() - this.days);
		d.setHours(0, 0, 0, 0);
		return exclude ? d.getTime() - 1 : d.getTime();
	}
}

export class InputPeriodOlder extends InputPeriodSince {

	getMoreThan(exclude: boolean, ctx: InputPeriod): number | null {return null}

	getLessThan(exclude: boolean, ctx: InputPeriod): number | null {
		const d = new Date();
		d.setDate(d.getDate() - this.days);
		d.setHours(0, 0, 0, 0);
		return exclude ? d.getTime() : d.getTime() - 1;
	}
}


/** après une date incluse. */
export class InputPeriodFrom extends InputPeriod_1_Action {

	getMoreThan(exclude: boolean, ctx: InputPeriod): number | null {
		const d = new Date(ctx.date1.value);
		d.setHours(0, 0, 0, 0);
		return exclude ? d.getTime() - 1 : d.getTime();
	}
}

/** avant une date exclue. */
export class InputPeriodBefore extends InputPeriod_1_Action {

	getLessThan(exclude: boolean, ctx: InputPeriod): number | null {
		const d = new Date(ctx.date1.value);
		d.setHours(0, 0, 0, 0);
		return exclude ? d.getTime() : d.getTime() - 1;
	}
}

/** entre deux dates incluses. */
export class InputPeriodFromTo extends InputPeriod_2_Action {

	getMoreThan(exclude: boolean, ctx: InputPeriod): number | null {
		const d = new Date(ctx.date1.value);
		d.setHours(0, 0, 0, 0);
		return exclude ? d.getTime() - 1 : d.getTime();
	}

	getLessThan(exclude: boolean, ctx: InputPeriod): number | null {
		const d = new Date(ctx.date2.value);
		d.setDate(d.getDate() + 1);
		d.setHours(0, 0, 0, 0);
		return exclude ? d.getTime() : d.getTime() - 1;
	}
}
